import './styles.scss';
import './styles.modules.scss';
import { Tab, Tabs } from '@material-ui/core';
import { HiLockClosed } from '@react-icons/all-files/hi/HiLockClosed';
// biome-ignore lint/style/useImportType: <explanation>
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'reactstrap';
import { SERVICE_NAME } from '~constants/app';
import type { Product } from '~domain/product/Product';
import { accountSelector } from '~redux/account/selectors/account';
import type { AuthUser } from '~redux/auth/actions/authUser';
import { signup } from '~redux/auth/reducers/signup';
import { authUserSelector } from '~redux/auth/selectors/authUser';
import type { Brand } from '~redux/brand/types';
import { isProtectedBrand } from '~utils/brand';
import { formatCurrency } from '~utils/formatter';
import { pushViewItemEvent } from '~utils/gtm';
import { getPublicImageUrl } from '~utils/image';
import { productViewed } from '~utils/segment';
import MagBrandLink from '../MagBrandLink';
import MagRelatedProducts from '../MagRelatedProducts';
import Preview from '../Preview';
import ProductCartForm from '../ProductCartForm';
import SendMessage from '../SendMessage';

type MagProductDetailProps = {
  product: Product;
};

type PreviewData = {
  src: string;
  thumbnailUrl: string;
  type: 'image' | 'video';
};

const MagProductDetail: React.FC<MagProductDetailProps> = ({ product }) => {
  const dispatch = useDispatch();
  const auth = useSelector(authUserSelector);
  const [previewData, setPreviewData] = useState<PreviewData>();
  const account = useSelector(accountSelector);
  const selectColor = useCallback(
    (color) => {
      const productType = product.producttype.find(
        (type) => type.color === color
      );
      if (!productType?.imageKey) {
        return;
      }
      setPreviewData({
        type: 'image',
        thumbnailUrl: productType.imageUrl!,
        src: productType.imageUrl!,
      });
    },
    [product.producttype]
  );

  const [tab, setTab] = useState<'content' | 'size' | 'description'>('content');
  const handleTab = useCallback((_, tab) => setTab(tab), []);

  const selectSize = useCallback(
    (size) => {
      const productType = product.producttype.find(
        (type) => type.size === size
      );
      if (!productType?.imageKey) {
        return;
      }
      setPreviewData({
        type: 'image',
        thumbnailUrl: productType.imageUrl!,
        src: productType.imageUrl!,
      });
    },
    [product.producttype]
  );

  const selectType = useCallback((type) => {
    const productType = product.producttype.find((t) => t.type_name === type);
    if (!productType?.imageKey) {
      return;
    }
    setPreviewData({
      type: 'image',
      thumbnailUrl: productType.imageUrl!,
      src: productType.imageUrl!,
    });
  }, []);

  const retailPrice = formatCurrency(product.product_retail_price);

  const singlePrice = formatCurrency(product.unitPrice);

  const handleSignup = useCallback(() => {
    dispatch(signup({ nextPath: window.location.pathname }));
  }, [product]);

  const handleLogin = useCallback(() => {
    dispatch(signup({ nextPath: window.location.pathname }));
  }, []);

  useEffect(() => {
    pushViewItemEvent(
      [
        {
          item_name: product.product_name,
          item_id: product.id,
          price: product.unitPrice,
          item_brand: product.brand.brand_name,
          item_category: product.product_category,
          item_category2: product.product_subcategory,
          index: 0,
          quantity: 1,
        },
      ],
      auth as AuthUser
    );
  }, []);

  useEffect(() => {
    if (!product) {
      return;
    }
    productViewed({
      brand: product.brand.brand_name,
      brand_id: product.product_brand_id,
      category: product.product_category,
      category2: product.product_subcategory,
      image_url: product.imageUrl,
      name: product.product_name,
      price: product.unitPrice,
      product_id: product.id,
      sku: product.product_number,
      url: `${document.location.protocol}//${document.location.host}/productdetail/${product.id}`,
    });
  }, [product]);

  // useEffect(() => {
  //   if (auth) return;
  //   const timer = setTimeout(() => dispatch(signup()), 3000);
  //   return () => {
  //     clearTimeout(timer);
  //   };
  // }, [auth, product]);

  const isProtected = useMemo(
    () => account && isProtectedBrand(product.brand as Brand, account),
    [product, account]
  );

  const [previews, setPreviews] = useState<PreviewData[]>([]);

  useEffect(() => {
    const exec = async () => {
      const images = product.imageKeys.map(({ imageUrl }) => ({
        src: imageUrl,
        thumbnailUrl: imageUrl,
        type: 'image' as const,
      }));

      const videos = await Promise.all(
        product.videos?.map(async ({ url }) => {
          return {
            src: url!,
            thumbnailUrl: url!,
            type: 'video' as const,
          };
        }) ?? []
      );
      setPreviews([...images, ...videos]);
    };

    exec();
  }, [product.imageKeys, product.videoKeys]);

  if (!product.brand) {
    return null;
  }

  return (
    <>
      <div className="product-details">
        <div className="py-3 px-4 border-bottom text-muted">
          <a href="/">Home</a> /{' '}
          <MagBrandLink brandId={product?.brand?.id}>
            {product?.brand?.brand_name}
          </MagBrandLink>{' '}
          / {product?.product_name}
        </div>
        <div className="product-content-top single-product py-4">
          <div styleName="grid">
            <div styleName="grid-item header">
              <span className="product-content-name">
                <MagBrandLink brandId={product.product_brand_id}>
                  <div className="d-flex mb-4 align-items-center">
                    <div className="mr-2">
                      <img
                        className="rounded-circle mr-2 border"
                        src={getPublicImageUrl(
                          product.brand.brand_icon_imageKey ?? undefined,
                          {
                            w: 60,
                            h: 60,
                          }
                        )}
                        style={{
                          width: '60px',
                          height: '60px',
                          objectFit: 'cover',
                        }}
                      />
                    </div>
                    <div>
                      <div className="font-weight-bold" translate="no">
                        {product.brand.brand_name}
                      </div>
                      {auth && (
                        <div className="text-black-50">
                          最小発注金額：
                          {product.brand.brand_minimum_buy
                            ? formatCurrency(product.brand.brand_minimum_buy)
                            : 'なし'}
                        </div>
                      )}
                    </div>
                  </div>
                </MagBrandLink>
                <h3 className="product_title entry-title mb-0">
                  {product.product_name}
                </h3>
                {auth && (
                  <div className="mt-2 text-black-50">
                    品番：{product.product_number}
                  </div>
                )}
              </span>
            </div>
            <div styleName="grid-item preview" className="product-top-left">
              <div
                className="product-top-left-inner"
                styleName="product-top-left-inner"
              >
                <Preview images={previews} image={previewData} />
              </div>
            </div>
            <div styleName="grid-item meta" className="product_meta">
              <div
                className="tab-pane fade show active"
                id="description"
                role="tabpanel"
                aria-labelledby="home-tab"
              >
                <Tabs
                  className="border-bottom mb-4"
                  value={tab}
                  onChange={handleTab}
                  TabIndicatorProps={{ style: { background: 'black' } }}
                >
                  <Tab
                    label="商品概要"
                    className="font-weight-bold"
                    value={'content'}
                  />
                  <Tab
                    label="素材詳細"
                    className="font-weight-bold"
                    value={'description'}
                  />
                  <Tab
                    label="サイズ詳細"
                    className="font-weight-bold"
                    value={'size'}
                  />
                </Tabs>
                {tab === 'content' && (
                  <>
                    <div className="font-weight-bold">
                      <label>カテゴリー：{product.product_category}</label>
                    </div>
                    <div className="font-weight-bold">
                      <label>
                        ブランド：
                        <MagBrandLink
                          brandId={product.brand.id}
                          style={{ textDecoration: 'underline' }}
                        >
                          {product.brand.brand_name}
                        </MagBrandLink>
                      </label>
                    </div>
                    {!isProtected && auth && (
                      <div className="font-weight-bold">
                        {product.product_preorder != 1 ? (
                          <label>
                            発送予定日：
                            {product.product_estimated_ship_date_min}〜
                            {product.product_estimated_ship_date_max}
                            営業日で発送予定
                          </label>
                        ) : (
                          <label>
                            発送予定日：{product.product_preorder_shipping_date}
                            に発送予定
                          </label>
                        )}
                      </div>
                    )}
                    <p
                      className="textarea-custom"
                      style={{ whiteSpace: 'pre-line' }}
                    >
                      {product.product_content}
                    </p>
                  </>
                )}
                {tab === 'description' && (
                  <p
                    className="textarea-custom"
                    style={{ whiteSpace: 'pre-line' }}
                  >
                    {product.product_description}
                  </p>
                )}
                {tab === 'size' && (
                  <p
                    className="textarea-custom"
                    style={{ whiteSpace: 'pre-line' }}
                  >
                    {product.product_size}
                  </p>
                )}
                {!isProtected && auth && (
                  <div styleName="contacts-wrapper">
                    <SendMessage
                      brandId={product.product_brand_id}
                      productName={product.product_name}
                      styleName="brand-contact-link"
                    >
                      ブランドへの問い合わせ
                    </SendMessage>
                  </div>
                )}
              </div>
            </div>
            <div styleName="grid-item details" className="product-top-right">
              <div className="product-top-right-inner">
                <div className="summary entry-summary">
                  {isProtected && (
                    <small className="product-return-message">
                      ご利用の地域では現在発注できません。
                    </small>
                  )}
                  {!isProtected && auth && (
                    <>
                      <div className="single-price mb-4">
                        <div className="price">
                          卸価格：{singlePrice}（税抜）
                        </div>
                        <div className="text-black-50">
                          <span>
                            希望小売価格：
                            {product.is_open_price
                              ? 'オープン価格'
                              : retailPrice}
                          </span>
                          {!product.is_open_price && (
                            <span>
                              （掛け率：
                              {product.productSaleWholesaleRate ? (
                                <>
                                  <span className="text-danger">
                                    {product.productSaleWholesaleRate}％
                                  </span>
                                  &nbsp;
                                  <del>{product.productWholesaleRate}％</del>
                                </>
                              ) : (
                                <>{product.productWholesaleRate}％</>
                              )}
                              ）
                            </span>
                          )}
                        </div>
                      </div>
                      <p>
                        <small>{product.brand.terms_of_sample}</small>
                      </p>
                    </>
                  )}
                  {!auth && (
                    <div styleName="public-details-wrapper">
                      <div styleName="public-details">
                        <div className="mb-4">
                          <p className="single-price mb-0">
                            <span className="price">
                              希望小売価格：{retailPrice}（税抜）
                            </span>
                          </p>
                          <a onClick={handleSignup}>
                            卸価格を表示
                            <HiLockClosed />
                          </a>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2">
                            <Button styleName="signup" onClick={handleSignup}>
                              {SERVICE_NAME}アカウントを作成（無料）
                            </Button>
                          </div>
                          <div>
                            すでに{SERVICE_NAME}アカウントをお持ちの方は
                            <a styleName="login" onClick={handleLogin}>
                              こちら
                            </a>
                            からログインください。
                          </div>
                        </div>
                        <div styleName="benefits">
                          <div>
                            <div styleName="benefits-title" className="mb-4">
                              <div>
                                <div className="mb-4">
                                  <b>
                                    {SERVICE_NAME}
                                    アカウント作成で以下の機能がご利用いただけます。
                                  </b>
                                </div>
                              </div>
                            </div>
                            <ul>
                              <li>卸売価格・お取引条件の閲覧</li>
                              <li>ブランドへのダイレクトメッセージ</li>
                              <li>ブランドへの仕入れ発注</li>
                            </ul>
                            <small className="text-black-50">
                              ご登録の際は
                              <a
                                href="https://www.homula.jp/terms/buyertermsofservice"
                                target="_blank"
                                rel="noreferrer"
                                style={{ textDecoration: 'underline' }}
                              >
                                ご利用規約
                              </a>
                              と
                              <a
                                href="https://www.homula.jp/terms/privacypolicy"
                                target="_blank"
                                rel="noreferrer"
                                style={{ textDecoration: 'underline' }}
                              >
                                プライバシーポリシー
                              </a>
                              をご確認ください。
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {!isProtected && auth && (
                    <>
                      <div className="mb-4">
                        <ProductCartForm
                          product={product}
                          productTypes={product.producttype}
                          onSelectColor={selectColor}
                          onSelectSize={selectSize}
                          onSelectType={selectType}
                          isDirect={false}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <MagRelatedProducts product={product} />
        </div>
      </div>
    </>
  );
};

export default MagProductDetail;
